import {
    ChangeDetectionStrategy,
    Component,
    contentChildren,
    ElementRef,
    input,
    Signal,
    viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * directives
 */
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';

/**
 * Wraps any block of content and, while `isEmpty` is true, blurs that content into a
 * non-interactive placeholder and centers a translated message over it.
 * Consumers can override the message via the named `messageTemplate` slot.
 */
@Component({
    selector: 'app-empty-overlay',
    templateUrl: './empty-overlay.component.html',
    styleUrl: './empty-overlay.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, TranslatePipe],
})
export class EmptyOverlayComponent {

    /** When true the projected content is blurred and the message overlay is shown. */
    public readonly isEmpty = input<boolean>(false);

    /** Translation key rendered in the overlay when no `messageTemplate` slot is provided. */
    public readonly messageKey = input<string>('GENERAL.NO_DATA');

    /** Blur radius applied to the projected content while the overlay is active. */
    public readonly blur = input<string>('1.8px');

    /**
     * The element the content sits in.
     *
     * Exposed so a host that builds this overlay at runtime can move existing
     * nodes into it — `projectableNodes` only fills the slot with nodes handed
     * over at creation, and cannot adopt a view that already stands elsewhere.
     */
    public readonly contentHost: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('contentHost');

    /** All `TemplateDirective` instances projected into the component's content. */
    protected readonly templateDirectives = contentChildren(TemplateDirective);

    /**
     * @description Finds a named template slot among projected `TemplateDirective` instances.
     * @param tempName - Slot name (e.g. `messageTemplate`).
     * @returns The directive instance or `undefined`.
     */
    protected getTemplate(tempName: string): TemplateDirective | undefined {
        const list = this.templateDirectives();
        return list.find((d) => d.name === tempName);
    }

}
